import React, { useState, useEffect } from 'react';
import { conversationAnnotationService } from '../../services/api';

const AnnotationsPanel = ({ conversationId, onClose, onAnnotationDeleted, onAddToTestPlan }) => {
  const [annotations, setAnnotations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingAnnotation, setEditingAnnotation] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    content: '',
    type: '',
    color: '',
    tags: ''
  });

  useEffect(() => {
    if (conversationId) {
      loadAnnotations();
    }
  }, [conversationId]);

  const loadAnnotations = async () => {
    try {
      setIsLoading(true);
      const data = await conversationAnnotationService.getConversationAnnotations(conversationId);
      setAnnotations(data);
    } catch (error) {
      console.error('Error loading annotations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (annotationId) => {
    if (window.confirm('Are you sure you want to delete this annotation?')) {
      try {
        await conversationAnnotationService.deleteAnnotation(annotationId);
        setAnnotations(annotations.filter(ann => ann.id !== annotationId));
        if (onAnnotationDeleted) {
          onAnnotationDeleted(annotationId);
        }
      } catch (error) {
        console.error('Error deleting annotation:', error);
        alert('Failed to delete annotation');
      }
    }
  };

  const handleEdit = (annotation) => {
    setEditingAnnotation(annotation);
    setEditForm({
      title: annotation.annotation_title,
      content: annotation.annotation_content || '',
      type: annotation.annotation_type,
      color: annotation.color,
      tags: annotation.tags.join(', ')
    });
  };

  const handleUpdate = async () => {
    try {
      const updateData = {
        annotation_title: editForm.title,
        annotation_content: editForm.content || null,
        annotation_type: editForm.type,
        color: editForm.color,
        tags: editForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
      };

      const updatedAnnotation = await conversationAnnotationService.updateAnnotation(
        editingAnnotation.id,
        updateData
      );

      setAnnotations(annotations.map(ann =>
        ann.id === editingAnnotation.id ? updatedAnnotation : ann
      ));

      setEditingAnnotation(null);
      setEditForm({ title: '', content: '', type: '', color: '', tags: '' });
    } catch (error) {
      console.error('Error updating annotation:', error);
      alert('Failed to update annotation');
    }
  };

  const handleCancelEdit = () => {
    setEditingAnnotation(null);
    setEditForm({ title: '', content: '', type: '', color: '', tags: '' });
  };

  const getAnnotationTypeLabel = (type) => {
    const typeMap = {
      'highlight': 'Highlight',
      'note': 'Note',
      'flag': 'Flag',
      'category': 'Category',
      'custom': 'Custom'
    };
    return typeMap[type] || type;
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4">
          <div className="flex justify-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-center mt-4 text-gray-600">Loading annotations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Conversation Annotations ({annotations.length})
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {annotations.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No annotations found for this conversation.</p>
          </div>
        ) : (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {annotations.map((annotation) => (
              <div
                key={annotation.id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                style={{ borderLeftColor: annotation.color, borderLeftWidth: '4px' }}
              >
                {editingAnnotation?.id === annotation.id ? (
                  // Edit form
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <textarea
                      value={editForm.content}
                      onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                    />
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={editForm.color}
                        onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                        className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={editForm.tags}
                        onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Tags separated by commas"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleUpdate}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display mode
                  <div>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{ backgroundColor: annotation.color + '20', color: annotation.color }}
                        >
                          {getAnnotationTypeLabel(annotation.annotation_type)}
                        </span>
                        <h4 className="font-medium text-gray-800">{annotation.annotation_title}</h4>
                      </div>
                      <div className="flex items-center space-x-2">
                        {onAddToTestPlan && (
                          <>
                            <button
                              onClick={() => onAddToTestPlan(annotation, 'append')}
                              className="text-teal-600 hover:text-teal-800 text-sm"
                            >
                              Add to plan
                            </button>
                            <button
                              onClick={() => onAddToTestPlan(annotation, 'editor')}
                              className="text-purple-600 hover:text-purple-800 text-sm"
                            >
                              Open in editor
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleEdit(annotation)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(annotation.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {annotation.annotation_content && (
                      <p className="text-gray-600 text-sm mb-2">{annotation.annotation_content}</p>
                    )}

                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center space-x-2">
                        <span>{annotation.selected_message_ids.length} message(s)</span>
                        {annotation.tags.length > 0 && (
                          <div className="flex items-center space-x-1">
                            <span>•</span>
                            {annotation.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-gray-100 rounded text-gray-600"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <span>{new Date(annotation.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnotationsPanel;
